/**
 * 抓包 API（辅助工具 → 抓包）
 */
import request, { get, post, put, del, getPage } from '@/utils/request'
import { downloadFile } from '@/api/aiTestCases'
import type { CaptureFlow, CaptureImportResult, CaptureRewriteRule, PageResponse } from '@/types'

/** 启动抓包代理 */
export const startCapture = (port?: number) => post<{ running: boolean; message: string }>('/capture/start', port ? { port } : {})

/** 停止抓包代理 */
export const stopCapture = () => post<{ running: boolean; message: string }>('/capture/stop')

/** 开始录制（代理未运行时自动拉起；录制中流量才入库） */
export const startRecording = () => post<{ recording: boolean }>('/capture/record/start')

/** 停止录制（暂停入库，代理保持运行） */
export const stopRecording = () => post<{ recording: boolean }>('/capture/record/stop')

/** 抓包记录分页列表（不返回 body；imported/unimported_count 为同筛选条件下的导入统计） */
export const getCaptureFlows = (params: {
  page?: number
  page_size?: number
  method?: string
  keyword?: string
  status_code?: number
  host?: string
  imported?: number
}) =>
  request.get<any, PageResponse<CaptureFlow> & { imported_count?: number; unimported_count?: number }>(
    '/capture/flows',
    { params },
  )

/** 抓包记录详情（含完整 body） */
export const getCaptureFlow = (id: number) => get<CaptureFlow>(`/capture/flows/${id}`)

/** 删除单条抓包记录 */
export const deleteCaptureFlow = (id: number) => del(`/capture/flows/${id}`)

/** 清空全部抓包记录 */
export const clearCaptureFlows = () => del('/capture/flows')

/** 一键导入接口列表 */
export const importCaptureFlows = (data: { flow_ids: number[]; project_id: number; module_id?: number | null }) =>
  post<CaptureImportResult>('/capture/import', data)

/** 下载 CA 证书（.crt 格式，Windows/macOS 双击即可进入安装向导） */
export const downloadCaCert = () => downloadFile('/api/v1/capture/ca-cert', 'auroratest-ca-cert.crt')

/** 请求重发（按 flow 原始请求 httpx 重放，结果作为新记录入库） */
export const repeatCaptureFlow = (id: number) =>
  post<{ flow_id: number; status_code: number; error_msg: string; duration_ms: number }>(`/capture/flows/${id}/repeat`)

/** 设置域名过滤（运行期内存态：off=不过滤 / allow=只抓白名单 / deny=屏蔽黑名单） */
export const setCaptureFilter = (data: { mode: 'off' | 'allow' | 'deny'; hosts: string[] }) =>
  post<{ mode: string; hosts: string[] }>('/capture/filter', data)

/** 上报平台自身地址：浏览器代理抓包时平台自身流量恒放行（不拦截不过滤不入库） */
export const reportCaptureSelfHost = (hosts: string[]) =>
  post<{ hosts: string[] }>('/capture/self-host', { hosts })

/** 设置弱网模拟（运行期内存态：请求/响应延迟 + 断网） */
export const setCaptureNetworkCondition = (data: { request_delay_ms: number; response_delay_ms: number; offline: boolean }) =>
  post<{ request_delay_ms: number; response_delay_ms: number; offline: boolean }>('/capture/network-condition', data)

/** 重写规则列表（sort_order 升序，含未启用） */
export const getRewriteRules = () => get<CaptureRewriteRule[]>('/capture/rewrite-rules')

/** 创建重写规则 */
export const createRewriteRule = (data: Partial<CaptureRewriteRule>) => post<CaptureRewriteRule>('/capture/rewrite-rules', data)

/** 更新重写规则（启停/编辑） */
export const updateRewriteRule = (id: number, data: Partial<CaptureRewriteRule>) =>
  put<CaptureRewriteRule>(`/capture/rewrite-rules/${id}`, data)

/** 批量排序重写规则 */
export const sortRewriteRules = (items: { id: number; sort_order: number }[]) =>
  post('/capture/rewrite-rules/sort', { items })

/** 删除重写规则 */
export const deleteRewriteRule = (id: number) => del(`/capture/rewrite-rules/${id}`)
